import { useContext } from 'react';
import Image from '@next/image';
import Head from 'next/head';

import config from 'config';
import { Article as ArticleType, SimilarArticle } from 'types/models/Article';
import withMetatags from 'core/hocs/withMetatags';
import { ConfigContext } from '@providers/ConfigProvider';
import { FilesContext } from '@providers/FilesProvider';
import { DescriptionDataContext } from '@providers/DescriptionDataProvider';
import AsideLayout from '@layouts/AsideLayout';
import Description, { checkDescriptionIsNotEmpty, getHeadings, getPlainTextContent } from '@templates/Description';
import Typography from '@components/atoms/Typography';
import Link from '@components/atoms/Link';
import ArticleInfo from '@components/ArticleInfo';
import BackButton from '@components/BackButton';
import HtmlPreview from '@components/HtmlPreview';
import ArticleDate from '@components/ArticleDate';
import SectionHeader from '@components/SectionHeader';
import ContentBox from '@components/atoms/ContentBox';
import TableOfContents from '@components/TableOfContents';
import { FormattedMessage } from 'utils/intl';
import getVideoStructuredData from 'utils/getVideoStructuredDate';
import getStructuredData from 'utils/getStructuredData';
import resolveImageUrl from 'utils/resolveImageUrl';
import { getDescriptionVideosIds } from 'utils/description';
import toLowerCase from 'utils/toLowerCase';
import getArticleDate from 'utils/getArticleDate';

import messages from './Article.messages';
import classes from './Article.module.scss';
import { getArticleStructuredData } from './Article.utils';

const { routes } = config;

type Props = Omit<ArticleType, 'teaser' | 'photos' | 'videos'> & {
  similarArticles: SimilarArticle[];
};

export const ArticleView = ({
  seoData,
  abstract,
  tags,
  slug,
  createdAt,
  updatedAt,
  publishedAt,
  imageFit,
  recommendedVariants,
  image,
  similarArticles,
}: Props): JSX.Element => {
  const { appUrl, contact } = useContext(ConfigContext);
  const { getShopImageConfigs } = useContext(FilesContext);
  const { descriptionArticlesVideos } = useContext(DescriptionDataContext);
  const title = seoData?.title;
  const content = seoData?.description;
  const metaDescription = seoData?.metaDescription;
  const [companyLogo] = getShopImageConfigs(['company-logo']);
  const companyLogoUrl = companyLogo ? resolveImageUrl(companyLogo, { appUrl }) : undefined;
  const isDescriptionNotEmpty = checkDescriptionIsNotEmpty(content);
  const [videoId] = isDescriptionNotEmpty ? getDescriptionVideosIds(content) : [];
  const video = descriptionArticlesVideos.find(({ id }) => id === videoId);

  const tableOfContents = getHeadings(content, 2)?.map((heading) => getPlainTextContent(heading, ' '));

  return (
    <>
      <Head>
        {seoData && (
          <>
            <title>{title}</title>
            <meta property="og:title" content={title} key="og-title" />
            <meta property="og:description" content={metaDescription} key="og-description" />
            <meta name="description" content={metaDescription} key="description" />
          </>
        )}
        <link rel="stylesheet" type="text/css" href="/prism.css" />
        {getStructuredData(
          getArticleStructuredData(
            { abstract, createdAt, updatedAt, image, seoData, tags },
            { appUrl, contact, companyLogoUrl }
          )
        )}
        {video && getStructuredData(getVideoStructuredData(video))}
      </Head>
      <AsideLayout withoutAside={!tableOfContents?.length}>
        <AsideLayout.Title>
          <Typography variant="huge" weight="400" renderAs="h1">
            {seoData?.header}
          </Typography>
          <ArticleInfo
            variant="long"
            className={classes.info}
            tags={tags}
            createdAt={createdAt}
            updatedAt={updatedAt}
            publishedAt={publishedAt}
            seoData={seoData}
            slug={slug}
            withShare
          />
          {image && (
            <ContentBox
              className={classes.thumbnail}
              variant="image"
              alt={slug}
              objectFit={toLowerCase(imageFit)}
              resolveConfig={{
                preset: 'article_thumbnail_main',
                extension: image.extension,
                name: image.basename,
                productName: 'article',
                description: 'thumbnail',
                width: image.width,
                height: image.height,
              }}
              hasLazyLoad={false}
              hasPreload
            />
          )}
        </AsideLayout.Title>
        <AsideLayout.Content>
          {content && (
            <div className={classes.content}>
              <Description data={content} tableOfContentsLevels={[2]} />
            </div>
          )}
        </AsideLayout.Content>
        {!!tableOfContents?.length && (
          <AsideLayout.Aside>
            {({ stickyClassName }) => <TableOfContents className={stickyClassName} items={tableOfContents} />}
          </AsideLayout.Aside>
        )}
      </AsideLayout>
      <div className={classes.bottom}>
        {!!similarArticles.length && (
          <div className={classes.similarArticles}>
            <SectionHeader renderAs="div">
              <FormattedMessage {...messages.similar} />
            </SectionHeader>
            {similarArticles.map((similarArticle) => {
              const { isUpdated, lastDateTimestamp } = getArticleDate(similarArticle);

              return (
                <Link
                  key={similarArticle.id}
                  component="span"
                  className={classes.similarArticle}
                  href={{ pathname: routes.article.name.href, query: { name: similarArticle.slug } }}
                >
                  <Link href={{ pathname: routes.article.name.href, query: { name: similarArticle.slug } }}>
                    <Typography className={classes.similarArticleTitle} variant="h4" weight="700" renderAs="div">
                      {similarArticle.seoData?.header}
                    </Typography>
                  </Link>
                  <Typography
                    className={classes.similarArticleDescription}
                    variant="h4"
                    renderAs="div"
                    ellipsisLines={2}
                  >
                    <Description data={similarArticle.teaser} renderFakeHeaders withoutLinks />
                  </Typography>
                  <ArticleDate
                    date={lastDateTimestamp ? new Date(lastDateTimestamp) : createdAt}
                    label={isUpdated ? <FormattedMessage {...messages.updated} /> : undefined}
                  />
                </Link>
              );
            })}
          </div>
        )}
        {!!recommendedVariants.length && (
          <div className={classes.recommendedVariants}>
            <SectionHeader renderAs="div">
              <FormattedMessage {...messages.recommended} />
            </SectionHeader>
            {recommendedVariants.map(({ id: variantId, name, thumbnail, productName, href }) => (
              <div key={variantId} className={classes.product}>
                <Image
                  objectFit="contain"
                  className={classes.image}
                  alt={name}
                  resolveConfig={{
                    preset: 'p_thumb_5',
                    extension: thumbnail.extension,
                    name: thumbnail.basename,
                    productName,
                    width: thumbnail.width,
                    height: thumbnail.height,
                  }}
                  hasLazyLoad={false}
                  hasFade={false}
                />
                <Link href={href} underline="hover">
                  <Typography variant="body2" renderAs="div">
                    <HtmlPreview htmlText={name} component="span" />
                  </Typography>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
      <BackButton className={classes.backButton} />
    </>
  );
};

export default withMetatags(ArticleView, { withCanonicalUrl: true });
